/**
 * AdCard — Ana səhifə elan grid-inin içərisindəki inline reklam kartı.
 *
 * Görünmə: yalnız reklam viewport-da ≥50% görünəndə sayılır (IntersectionObserver).
 * Klik: hər klik backend-ə qeydə alınır.
 */
import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import api from '../api/axios'
import { useAuth } from '../context/AuthContext'
import { safeUrl } from '../utils/safeUrl'

function AdWrapper({ url, adId, children }) {
  const handleClick = () => {
    api.post(`/ads/${adId}/click`).catch(() => {})
  }

  const href = safeUrl(url)
  if (!href) {
    return <span style={{ textDecoration: 'none', color: 'inherit' }}>{children}</span>
  }
  if (href.startsWith('/')) {
    return (
      <Link to={href} style={{ textDecoration: 'none', color: 'inherit' }} onClick={handleClick}>
        {children}
      </Link>
    )
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
       style={{ textDecoration: 'none', color: 'inherit' }} onClick={handleClick}>
      {children}
    </a>
  )
}

export default function AdCard({ ad }) {
  const { user } = useAuth()
  const ref     = useRef(null)
  const viewed  = useRef(false)
  const timerRef = useRef(null)

  useEffect(() => {
    if (!ad || viewed.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          // 1 saniyə görünəndə say
          timerRef.current = setTimeout(() => {
            if (!viewed.current) {
              viewed.current = true
              api.post(`/ads/${ad.id}/view`).catch(() => {})
            }
          }, 1000)
        } else {
          clearTimeout(timerRef.current)
        }
      },
      { threshold: 0.5 }
    )

    if (ref.current) observer.observe(ref.current)
    return () => {
      observer.disconnect()
      clearTimeout(timerRef.current)
    }
  }, [ad?.id])

  if (!ad || user?.vip) return null

  return (
    <AdWrapper url={ad.targetUrl} adId={ad.id}>
      <div
        ref={ref}
        style={{
          borderRadius: 'var(--radius)',
          overflow: 'hidden',
          position: 'relative',
          border: '1px solid rgba(var(--accent-rgb),.2)',
          background: 'rgba(var(--accent-rgb),.03)',
          cursor: 'pointer',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'border-color .2s, box-shadow .2s',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.borderColor = 'rgba(var(--accent-rgb),.45)'
          e.currentTarget.style.boxShadow = '0 0 20px rgba(var(--accent-rgb),.08)'
        }}
        onMouseLeave={e => {
          e.currentTarget.style.borderColor = 'rgba(var(--accent-rgb),.2)'
          e.currentTarget.style.boxShadow = 'none'
        }}
      >
        {/* Reklam etiketi */}
        <div style={{
          position: 'absolute', top: 10, right: 10, zIndex: 2,
          fontSize: '.6rem', fontWeight: 700, letterSpacing: '.1em',
          padding: '.2rem .55rem', borderRadius: '100px',
          background: 'rgba(var(--accent-rgb),.12)',
          border: '1px solid rgba(var(--accent-rgb),.25)',
          color: 'var(--accent)',
          textTransform: 'uppercase',
        }}>
          Reklam
        </div>

        {/* Şəkil — responsive */}
        <picture style={{ display: 'block', width: '100%' }}>
          <source media="(min-width: 1024px)" srcSet={ad.imageDesktop} />
          <source media="(min-width: 640px)" srcSet={ad.imageTablet} />
          <img loading="lazy"
            src={ad.imageMobile}
            alt={ad.title}
            style={{ width: '100%', height: 'auto', display: 'block', maxHeight: 180, objectFit: 'cover' }}
          />
        </picture>

        {/* Alt məlumat */}
        <div style={{ padding: '1rem 1.25rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
          <div style={{ fontWeight: 700, fontSize: '.9rem', color: 'var(--text)' }}>{ad.title}</div>
          <div style={{
            fontSize: '.78rem', color: 'var(--muted)', lineHeight: 1.55,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {ad.description}
          </div>
          <div style={{ marginTop: 'auto', paddingTop: '.75rem' }}>
            <span style={{ fontSize: '.72rem', color: 'var(--muted)' }}>{ad.advertiserName}</span>
          </div>
        </div>
      </div>
    </AdWrapper>
  )
}
